import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label in the dropdown. */
  hint?: string;
  /** When true, renders as a disabled option. */
  disabled?: boolean;
}

export interface SearchableSelectProps {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Min width in px of the trigger button. Default 160. */
  minWidth?: number;
  /** Max width of the trigger button. Default unset (grows with content). */
  maxWidth?: number;
  /** Apply a compact visual style (smaller padding/font). */
  size?: "default" | "compact" | "minimal";
  /** Placeholder text shown inside the search input. */
  searchPlaceholder?: string;
  /** Accessible label. */
  ariaLabel?: string;
  /** Extra className appended to the trigger for custom positioning. */
  className?: string;
  /** Disable the built-in search input (useful for short lists). */
  searchable?: boolean;
  /** Optional id for label association. */
  id?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  placement: "bottom" | "top";
}

const MIN_POPOVER_WIDTH = 220;
const MAX_POPOVER_HEIGHT = 340;

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled = false,
  minWidth = 160,
  maxWidth,
  size = "default",
  searchPlaceholder = "Search…",
  ariaLabel,
  className,
  searchable = true,
  id,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) => {
      const hay = `${opt.label} ${opt.hint ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const needed = Math.min(MAX_POPOVER_HEIGHT, 260);
    const placement: "bottom" | "top" =
      spaceBelow < needed && spaceAbove > spaceBelow ? "top" : "bottom";
    const width = Math.max(rect.width, MIN_POPOVER_WIDTH);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setPosition({
      top: placement === "bottom" ? rect.bottom + 6 : rect.top - 6,
      left: Math.max(8, left),
      width,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function onScrollOrResize() {
      updatePosition();
    }
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        popoverRef.current &&
        !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    // Focus the search input after the popover renders.
    const t = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((opt) => opt.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, filtered, value]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex((prev) => {
      if (filtered.length === 0) return 0;
      if (prev >= filtered.length) return 0;
      return prev;
    });
  }, [filtered, open]);

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function commit(option: SearchableSelectOption | undefined) {
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(Math.max(0, filtered.length - 1));
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  const sizeClass =
    size === "compact"
      ? " searchable-select--compact"
      : size === "minimal"
        ? " searchable-select--minimal"
        : "";

  const triggerLabel = selectedOption?.label ?? placeholder;
  const hasValue = !!selectedOption;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`searchable-select__trigger${hasValue ? "" : " is-placeholder"}${open ? " is-open" : ""}${sizeClass}${className ? ` ${className}` : ""}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        style={{
          minWidth: `${minWidth}px`,
          ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
        }}
      >
        <span className="searchable-select__value">{triggerLabel}</span>
        <svg
          className="searchable-select__caret"
          viewBox="0 0 24 24"
          width="11"
          height="11"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            className={`searchable-select__popover searchable-select__popover--${position.placement}`}
            style={{
              top:
                position.placement === "bottom"
                  ? `${position.top}px`
                  : undefined,
              bottom:
                position.placement === "top"
                  ? `${window.innerHeight - position.top}px`
                  : undefined,
              left: `${position.left}px`,
              width: `${position.width}px`,
            }}
            role="dialog"
          >
            {searchable && (
              <div className="searchable-select__search">
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    filtered[activeIndex]
                      ? `${listboxId}-opt-${activeIndex}`
                      : undefined
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            )}
            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              className="searchable-select__list"
            >
              {filtered.length === 0 && (
                <div className="searchable-select__empty">No matches</div>
              )}
              {filtered.map((opt, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = opt.value === value;
                return (
                  <div
                    key={opt.value}
                    id={`${listboxId}-opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    data-index={idx}
                    className={`searchable-select__option${isActive ? " is-active" : ""}${isSelected ? " is-selected" : ""}${opt.disabled ? " is-disabled" : ""}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(e) => {
                      // Prevent blur of the search input before click registers.
                      e.preventDefault();
                    }}
                    onClick={() => commit(opt)}
                  >
                    <span className="searchable-select__option-body">
                      <span className="searchable-select__option-label">
                        {opt.label}
                      </span>
                      {opt.hint && (
                        <span className="searchable-select__option-hint">
                          {opt.hint}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <svg
                        className="searchable-select__option-check"
                        viewBox="0 0 24 24"
                        width="13"
                        height="13"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
